import { TASK_CARD_BRAND_LOGO } from '@/app/[locale]/tasks/components/TaskCard/taskCard.constants'
import type { TaskCardDemoRow } from '@/app/[locale]/tasks/components/TasksTaskCards/tasksTaskCards.constants'
import type { WithTaskGroup } from '@/features/tasks/api/taskGroupsApi'
import {
  type AvailableTaskCard,
  pickVideoUrl
} from '@/features/tasks/api/watchTaskApi'
import { applyTaskGroupToRow } from '@/features/tasks/mappers/applyTaskGroupToRow'
import { pickTaskCoverUrl } from '@/features/tasks/mappers/pickTaskCoverUrl'
import { readActionPlatforms } from '@/features/tasks/mappers/readActionPlatforms'
import { normalizeShareAvailableFailureReason } from '@/features/tasks/share/shareAvailableTaskList'
import {
  getTaskCardDemoTitle,
  mapTaskTypeToCta
} from '@/features/tasks/taskCardCta'

type AvailableTaskCardPayload = AvailableTaskCard & {
  brandName?: string
  brandLogoUrl?: string
  campaignName?: string
  thumbnailUrl?: string
  videoUrl?: string
  availableUntil?: string
}

export function firstNonEmptyTrimmed(
  ...candidates: Array<string | undefined | null>
): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) {
      return c.trim()
    }
  }
  return ''
}

function parseRewardPoints(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.floor(raw))
  }
  if (typeof raw === 'string') {
    const m = raw.match(/\d+/)
    if (m) {
      return Math.max(0, Number.parseInt(m[0], 10))
    }
  }
  return 0
}

function hashToCoverIndex(taskId: string): 0 | 1 | 2 | 3 {
  let h = 0
  for (let i = 0; i < taskId.length; i++) {
    h = (h + taskId.charCodeAt(i)) % 997
  }
  return (h % 4) as 0 | 1 | 2 | 3
}

function pickCoverUrl(task: AvailableTaskCardPayload): string | null {
  return pickTaskCoverUrl({
    thumbnailUrl: firstNonEmptyTrimmed(task.thumbnail_url, task.thumbnailUrl),
    instruction: task.instruction ?? undefined,
    preferThumbnail: mapTaskTypeToCta(task.type) === 'watch'
  })
}

export function mapTaskCardSocialPlatform(
  platform: string | undefined
): TaskCardDemoRow['social'] {
  const p = (platform ?? '').trim().toLowerCase()
  if (p.includes('tiktok')) {
    return 'tiktok'
  }
  if (p.includes('instagram')) {
    return 'instagram'
  }
  if (p.includes('youtube')) {
    return 'youtube'
  }
  if (p.includes('facebook')) {
    return 'facebook'
  }
  if (p === 'x' || p.includes('twitter')) {
    return 'x'
  }
  return null
}

export function hoursLeftFromAvailableUntil(
  availableUntil: string | undefined
): number {
  if (!availableUntil) {
    return 24 * 30
  }
  const untilMs = new Date(availableUntil).getTime()
  if (!Number.isFinite(untilMs)) {
    return 24 * 30
  }
  const remainingMs = untilMs - Date.now()
  return Math.max(0, remainingMs / (1000 * 60 * 60))
}

export function mapAvailableTaskCardToTaskRow(
  task: AvailableTaskCard & WithTaskGroup
): TaskCardDemoRow {
  const row = mapTaskCardToRow(task)
  const group = task.task_group
  if (!group) {
    return row
  }
  return applyTaskGroupToRow(
    row,
    mapTaskCardToRow(group.brief),
    group.platforms.map(p => mapTaskCardToRow(p.task))
  )
}

function mapTaskCardToRow(task: AvailableTaskCard): TaskCardDemoRow {
  const p = task as AvailableTaskCardPayload
  const cta = mapTaskTypeToCta(task.type)
  const coverUrl = pickCoverUrl(p)
  const displayTitle = firstNonEmptyTrimmed(
    p.title,
    p.campaignName,
    p.campaign_name
  )
  const displayDescription = firstNonEmptyTrimmed(p.description)
  const brandName = firstNonEmptyTrimmed(p.brand_name, p.brandName)
  const brandLogoUrl = firstNonEmptyTrimmed(p.brand_logo_url, p.brandLogoUrl)
  const fanPts = parseRewardPoints(p.fan_reward)
  const rewardPts = parseRewardPoints(p.reward)
  const rewardPoints = Math.max(fanPts, rewardPts)
  const volume = Math.max(0, Math.floor(Number(p.volume) || 0))
  const claimedCount = Math.max(0, Math.floor(Number(p.claimed_count) || 0))
  const availableUntilRaw =
    task.available_until ?? p.availableUntil ?? undefined
  const watchVideoUrl =
    pickVideoUrl(task) ?? pickVideoUrl(task.instruction ?? undefined)
  return {
    id: task.task_id,
    rewardPoints,
    hoursLeft: hoursLeftFromAvailableUntil(availableUntilRaw),
    coverI: hashToCoverIndex(task.task_id),
    coverUrl,
    participantCurrent: claimedCount,
    participantLimit: Math.max(1, volume || 1),
    popularityScore: claimedCount,
    social: mapTaskCardSocialPlatform(task.platform),
    cta,
    reserved: false,
    title: getTaskCardDemoTitle(cta),
    apiTitle: displayTitle || undefined,
    apiDescription: displayDescription || undefined,
    apiBrandName: brandName || undefined,
    apiBrandLogoUrl: brandLogoUrl ? brandLogoUrl : TASK_CARD_BRAND_LOGO,
    apiBrandResources: p.brand_resources,
    watchVideoUrl: watchVideoUrl || undefined,
    isRetry: task.is_retry === true,
    failureReason: normalizeShareAvailableFailureReason(task.failure_reason),
    actionPlatforms:
      cta === 'action' ? readActionPlatforms(task.action_details) : undefined
  }
}
